import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { MailCheck, RefreshCw, AlertTriangle, ServerCog } from 'lucide-react';
import { Button } from '../ui/button';

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch { return iso; }
};

export const SendAuditCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setData((await api.sendAudit()).data); } catch { /* non-admin or offline */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (!data && loading) return null;
  if (!data) return null;

  const activeCount = data.active_instances || 0;

  return (
    <div className="bg-card border border-border rounded-sm p-6 space-y-4" data-testid="send-audit-card">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg text-foreground flex items-center gap-2">
          <MailCheck className="w-5 h-5 text-emerald-400" /> Email Send Audit
        </h2>
        <Button onClick={load} disabled={loading} size="sm" variant="ghost" className="text-xs h-7" data-testid="send-audit-refresh-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="border border-border rounded-sm p-3">
          <p className="text-[10px] text-muted-foreground/50 uppercase tracking-widest font-bold">Backend build</p>
          <p className="text-sm font-mono text-foreground mt-1" data-testid="send-audit-build">{data.backend_build}</p>
        </div>
        <div className="border border-border rounded-sm p-3">
          <p className="text-[10px] text-muted-foreground/50 uppercase tracking-widest font-bold">Active scheduler instances</p>
          <p className={`text-sm font-mono mt-1 ${activeCount > 1 ? 'text-amber-400' : 'text-emerald-400'}`} data-testid="send-audit-instances">
            {activeCount} {activeCount > 1 ? '(multiple workers — locks protect sends)' : ''}
          </p>
        </div>
        <div className="border border-border rounded-sm p-3">
          <p className="text-[10px] text-muted-foreground/50 uppercase tracking-widest font-bold">Duplicate suspicion (7d)</p>
          <p className={`text-sm font-mono mt-1 ${data.duplicates?.length ? 'text-red-400' : 'text-emerald-400'}`} data-testid="send-audit-duplicates-count">
            {data.duplicates?.length || 0}
          </p>
        </div>
      </div>

      {data.instances?.length > 0 && (
        <div>
          <p className="text-[10px] text-muted-foreground/50 uppercase tracking-widest font-bold mb-2 flex items-center gap-1">
            <ServerCog className="w-3 h-3" /> Instances (48h)
          </p>
          <div className="space-y-1">
            {data.instances.map((i, idx) => (
              <div key={idx} className="flex items-center gap-3 text-xs font-mono" data-testid={`send-audit-instance-${idx}`}>
                <span className={`w-2 h-2 rounded-full shrink-0 ${i.active ? 'bg-emerald-400' : 'bg-muted-foreground/30'}`} />
                <span className="text-foreground">{i.instance}</span>
                <span className="text-muted-foreground">build {i.build}</span>
                <span className="text-muted-foreground">last seen {fmtTime(i.last_seen)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.duplicates?.length > 0 && (
        <div className="border border-red-900/40 bg-red-950/20 rounded-sm p-3 space-y-2" data-testid="send-audit-duplicates">
          <p className="text-xs text-red-400 font-medium flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> Same email sent more than once (last 7 days)
          </p>
          {data.duplicates.map((d, idx) => (
            <div key={idx} className="text-xs space-y-0.5">
              <p className="text-foreground font-medium">{d.count}× "{d.subject}" → {(d.to || []).join(', ')}</p>
              {(d.sends || []).map((s, j) => (
                <p key={j} className="text-muted-foreground font-mono pl-3">{fmtTime(s.at)} · {s.job} · {s.instance}</p>
              ))}
            </div>
          ))}
        </div>
      )}

      <div>
        <p className="text-[10px] text-muted-foreground/50 uppercase tracking-widest font-bold mb-2">Last outgoing emails</p>
        {(!data.sends || data.sends.length === 0) ? (
          <p className="text-xs text-muted-foreground">No sends logged yet — every automatic and manual email will appear here from now on.</p>
        ) : (
          <div className="max-h-72 overflow-y-auto border border-border rounded-sm">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-secondary">
                <tr className="text-left text-muted-foreground">
                  <th className="px-2 py-1.5 font-medium">Time</th>
                  <th className="px-2 py-1.5 font-medium">Job</th>
                  <th className="px-2 py-1.5 font-medium">To</th>
                  <th className="px-2 py-1.5 font-medium">Subject</th>
                  <th className="px-2 py-1.5 font-medium">Instance</th>
                  <th className="px-2 py-1.5 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.sends.map((s, idx) => (
                  <tr key={idx} className="border-t border-border/50" data-testid={`send-audit-row-${idx}`}>
                    <td className="px-2 py-1 font-mono whitespace-nowrap text-muted-foreground">{fmtTime(s.at)}</td>
                    <td className="px-2 py-1 text-foreground">{s.job}</td>
                    <td className="px-2 py-1 text-muted-foreground truncate max-w-[160px]">{(s.to || []).join(', ')}</td>
                    <td className="px-2 py-1 text-foreground truncate max-w-[220px]">{s.subject}</td>
                    <td className="px-2 py-1 font-mono text-muted-foreground whitespace-nowrap">{s.instance}</td>
                    <td className={`px-2 py-1 font-medium ${s.ok ? 'text-emerald-400' : 'text-red-400'}`}>{s.ok ? 'sent' : (s.error || 'failed')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
